import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';
import PersonAvatar from '../../events/shared/PersonAvatar';

/**
 * PromotionEventRow - Renders a single promotion event (new associated person)
 */
const PromotionEventRow = React.memo(function PromotionEventRow({
  event,
  isChild = false,
  address,
  onLocationClick
}) {
  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata) {
    return null;
  }

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const croppedFaceUrl = getCapturedImageDisplayUrl(event.metadata.croppedFaceUrl);
  const timestamp = new Date(event.timestamp || event.createdAt);
  const associatedPersonId = event.metadata.associatedPersonId || 'Unknown';
  const proximityLevel = event.metadata.proximityLevel;
  const confidence = event.metadata.confidence || 95; // Face detection confidence (0-100), default to 95 if missing

  console.log('[PromotionEventRow] Event metadata:', {
    eventId: event.eventId,
    confidence: event.metadata.confidence,
    metadata: event.metadata
  });

  // Determine background color
  const backgroundColor = isChild ? '#f5f5f5' : '#fff';
  const borderColor = isChild ? '4px solid #ce93d8' : 'none';

  // Proximity chip color
  const getProximityColor = (level) => {
    switch (level) {
      case 'HIGH': return 'success';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title="New Person">
          <PersonAddIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty for promotions) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* AI match indicator with confidence circle */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0
          }}>
            {/* AI icon */}
            <AutoAwesomeIcon sx={{ color: 'secondary.main', fontSize: 20 }} />

            {/* Confidence circle */}
            <Box sx={{
              position: 'relative',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `conic-gradient(${
                confidence >= 99 ? '#2e7d32' :
                confidence >= 90 ? '#2e7d32' :
                confidence >= 80 ? '#c77700' :
                '#b71c1c'
              } ${(confidence / 100) * 360}deg, rgba(0, 0, 0, 0.1) 0deg)`
            }}>
              <Box sx={{
                width: 23,
                height: 23,
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.5rem',
                    color:
                      confidence >= 99 ? '#2e7d32' :
                      confidence >= 90 ? '#2e7d32' :
                      confidence >= 80 ? '#c77700' :
                      '#b71c1c',
                    lineHeight: 1
                  }}
                >
                  {Math.round(confidence)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Associated person face thumbnail - prefer cropped face over full capture */}
          {(croppedFaceUrl || capturedImageUrl) && (
            <Box sx={{
              border: '3px solid',
              borderColor: 'secondary.main',
              borderRadius: '50%',
              padding: '2px',
              display: 'inline-flex',
              flexShrink: 0,
              '& .MuiAvatar-root': { mr: 0 }
            }}>
              <PersonAvatar
                imageUrl={croppedFaceUrl || capturedImageUrl}
                name={associatedPersonId}
                size={60}
              />
            </Box>
          )}

          {/* Event info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {event.description || 'New person detected'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {associatedPersonId}
              </Typography>
              {proximityLevel && (
                <Chip
                  label={proximityLevel}
                  size="small"
                  color={getProximityColor(proximityLevel)}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </TableCell>

      {/* Timestamp */}
      <TableCell sx={{ width: 140 }}>
        <Typography variant="caption" color="text.secondary">
          {timestamp.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {timestamp.toLocaleDateString('en-GB')}
        </Typography>
      </TableCell>

      {/* Location */}
      <TableCell sx={{ width: 80 }} align="center">
        {event.location && (
          <Tooltip title={address || 'Show location'}>
            <IconButton
              size="small"
              onClick={() => onLocationClick?.(event.location)}
              sx={{ color: 'primary.main' }}
            >
              <LocationOnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
});

export default PromotionEventRow;
